/**
 * Treść wiadomości w ZE (notatka kamienia, komentarz zadania KPI) z podświetlonymi
 * @oznaczeniami. Wątki mają wpisy sprzed wprowadzenia edytora (czysty tekst) i nowe
 * (HTML z edytora), więc render rozpoznaje jedno i drugie — tak samo jak dyskusja
 * pod przeglądem KPI.
 */
import { useCallback, useState } from "react";
import DOMPurify from "dompurify";
import { highlightMentions, highlightMentionsInHtml } from "@/utils/highlightMentions";

function isHtmlContent(content: string) {
  return content.trimStart().startsWith("<");
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "h1", "h2", "h3", "a", "img"],
    ALLOWED_ATTR: ["start", "href", "target", "rel", "class", "src", "alt", "width", "height"],
  });
}

export function CommentBody({
  content,
  mentions = [],
  className = "text-sm text-[var(--color-text-secondary)]",
}: {
  content: string;
  mentions?: { full_name: string }[];
  className?: string;
}) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const names = mentions.map((m) => m.full_name);

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "IMG") {
      setLightboxSrc((target as HTMLImageElement).src);
    }
  }, []);

  if (isHtmlContent(content)) {
    return (
      <>
        <div
          className={`rich-text-content ${className}`}
          dangerouslySetInnerHTML={{ __html: highlightMentionsInHtml(sanitize(content), names) }}
          onClick={handleClick}
        />
        {lightboxSrc && (
          <div
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80"
            onClick={() => setLightboxSrc(null)}
          >
            <img
              src={lightboxSrc}
              alt=""
              className="max-h-[90vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className="absolute right-4 top-4 text-2xl font-bold text-white hover:text-gray-300"
              onClick={() => setLightboxSrc(null)}
            >
              ×
            </button>
          </div>
        )}
      </>
    );
  }
  return (
    <p className={`whitespace-pre-wrap break-words ${className}`}>
      {highlightMentions(content, names)}
    </p>
  );
}
